import KenSelect from '../../../../GlobalComponents/KenSelect';
import { Box, Button, Divider, InputAdornment, Theme, Grid, Typography, Select, MenuItem, SelectChangeEvent, TextField, IconButton } from '@mui/material';
import KenInput from '../../../../GlobalComponents/KenInput';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MicIcon from '@mui/icons-material/Mic';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { SearchBarProps, searchBarData } from '../../../../interfaces/Common';
import { makeStyles } from '@mui/styles';
import { useStyles as JobStyles } from '../../styles';
import { useEffect, useState } from 'react';
import KenColors from '../../../../Helpers/Themes/kenColors';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => ({
  searchContainer: {
    display: 'flex',
    gap: '10px',
    border: '1px solid #B2B3B7',
    alignItems: 'center',
    borderRadius: '9px',
  },
}));

const SearchBar: React.FunctionComponent<SearchBarProps> = ({ handleFilter, dropDownData, handleDropDownChange, handleSearchChange, searchBarData }) => {
  const classes = useStyles();
  const jobClasses = JobStyles();
  const [data, setData] = useState<searchBarData>({ dropDown: '', input: '' });
  const [isListening, setIsListening] = useState(false);
  const { pathname } = useLocation();
  const pathSegments: string[] = pathname.split('/').filter((items: string) => items && items !== '');
  const fullPath = pathSegments.join('/');
  useEffect(() => {
    if (searchBarData) {
      setData({ ...searchBarData });
    }
  }, [searchBarData]);

  const handleMicClick = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech Recognition is not supported in this browser.');
      return;
    }

    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      console.log('Voice recognition started');
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      console.log('Transcript:', transcript);
      handleSearchChange(transcript); // Update the search input with the recognized text
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      alert('Error occurred during speech recognition. Please try again.');
    };

    recognition.onend = () => {
      setIsListening(false);
      console.log('Voice recognition ended');
    };

    recognition.start();
  };

  return (
    <Box padding={'20px'} className="filter-cells-container scrollbar-hide">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} lg={10} xl={10} md={10}>
          <Box
            sx={{
              height: '36px',
              width: '100%',
              '@media (min-width: 1280px) and (max-width: 1300px)': {
                height: '30px',
              },
            }}
            display={'flex'}
            justifyContent={'start'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #B2B3B7', borderRadius: '6px 0px 0px 6px' }}>
              <InputAdornment position="start">
                <LocationOnOutlinedIcon
                  sx={{
                    fontSize: '18px',
                    marginLeft: '5px',
                    '@media (min-width: 1280px) and (max-width: 1300px)': {
                      fontSize: '15px',
                    },
                  }}
                />
              </InputAdornment>
              {dropDownData && handleDropDownChange && (
                <Select
                  labelId="custom-select-label"
                  value={data?.dropDown || ''}
                  onChange={(event: SelectChangeEvent<string>) => {
                    const value = event.target.value;
                    console.log('Selected value:', value);

                    if (value !== '') {
                      handleDropDownChange(value);
                    }
                  }}
                  sx={{
                    height: '36px',
                    fontSize: '12px',
                    borderRadius: '6px 0px 0px 6px',
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3061FF',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover': {
                      border: 'none',
                    },
                    '&.Mui-focused': {
                      border: 'none',
                    },
                    '@media (min-width: 1280px) and (max-width: 1300px)': {
                      height: '30px',
                      fontSize: '10px',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          fontSize: '12px',
                        },
                      },
                    },
                  }}
                >
                  {dropDownData.map((location: any) => {
                    return <MenuItem value={location}>{location}</MenuItem>;
                  })}
                </Select>
              )}
            </Box>
          <TextField
  placeholder="Search by company or job"
  variant="outlined"
  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target) {
      handleSearchChange(e.target.value);
    }
  }}
  value={data?.input || ''}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton edge="end" onClick={handleMicClick}>
          <MicIcon
            style={{
              fontSize: '18px',
              color: isListening ? 'red' : 'inherit',
            }}
          />
        </IconButton>
      </InputAdornment>
    ),
    sx: {
      height: '36px',
      borderRadius: '0px 6px 6px 0px',
      '@media (min-width: 1280px) and (max-width: 1300px)': {
        height: '30px',
      },
    },
  }}
  sx={{
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '0px 6px 6px 0px',
      height: '36px',
      fontSize: '12px',
      '& fieldset': {
        borderColor: '#ccc', // default
      },
      '&:hover fieldset': {
        borderColor: '#2C60AA', // on hover
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2C60AA', // ✅ on focus
        borderWidth: '1px',
      },
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: '12px',
    },
    '@media (min-width: 1280px) and (max-width: 1300px)': {
      '& .MuiOutlinedInput-root': {
        height: '30px',
        fontSize: '10px',
      },
    },
  }}
/>

          </Box>
        </Grid>
        {fullPath === 'jobs' ? null :
          <Grid item xs={12} sm={12} lg={2} xl={2} md={2} sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button
              sx={{
                textTransform: 'capitalize',
                height: '36px',
                backgroundColor: '#FEF6E6',
                '@media (min-width: 1280px) and (max-width: 1300px)': {
                  height: '30px',
                },
              }}
              size="small"
              onClick={() => {
                handleFilter();
              }}
              variant="contained"
              startIcon={
                <FilterAltOutlinedIcon
                  sx={{
                    fontSize: '14px',
                    color: KenColors.ClientBlack2,
                    '@media (min-width: 1280px) and (max-width: 1300px)': {
                      fontSize: '12px',
                    },
                  }}
                />
              }
              className={jobClasses.filterBtn}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  '@media (min-width: 1280px) and (max-width: 1300px)': {
                    fontSize: '10px',
                  },
                }}
              >
                Filters
              </Typography>
            </Button>
          </Grid>
        }
      </Grid>
    </Box>
  );
};

export default SearchBar;
